import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useMutation } from '@tanstack/react-query';
import { Bell, Check, Trash2, UserCheck, AlertCircle, MessageSquare, X } from 'lucide-react';
import { api } from '@/api/client';
import { useNotificationStore } from '@/stores/notificationStore';
import { Notification } from '@/types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useFormatDistanceToNow } from '@/hooks/useFormatDistanceToNow';

interface NotificationDropdownProps {
  onNotificationClick?: (notification: Notification) => void;
}

function NotificationPanelHeader(props: {
  unreadCount: number;
  isMobile: boolean;
  onClose: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const { unreadCount, isMobile, onClose, t } = props;
  return (
    <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
      <div className="flex items-center gap-2">
        <Bell className="w-5 h-5" />
        <h2 className="text-lg font-semibold">{t('header.notifications')}</h2>
        {isMobile && unreadCount > 0 && (
          <span className="px-2 py-0.5 text-xs font-medium bg-destructive text-destructive-foreground rounded-full">
            {unreadCount}
          </span>
        )}
      </div>
      {isMobile && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 cursor-pointer"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <X className="w-5 h-5" />
        </Button>
      )}
    </div>
  );
}

function NotificationToolbar(props: {
  notifications: Notification[];
  selectedNotificationIds: string[];
  unreadCount: number;
  onSelectAll: () => void;
  onMarkAllRead: () => void;
  onMarkSelectedAsRead: () => void;
  onDeleteSelected: () => void;
  markAllReadPending: boolean;
  markReadPending: boolean;
  deleteMultiplePending: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const {
    notifications,
    selectedNotificationIds,
    unreadCount,
    onSelectAll,
    onMarkAllRead,
    onMarkSelectedAsRead,
    onDeleteSelected,
    markAllReadPending,
    markReadPending,
    deleteMultiplePending,
    t,
  } = props;
  if (notifications.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-between p-3 border-b border-border bg-muted/50 gap-2 shrink-0">
      <div className="flex items-center gap-3 min-w-0">
        <Checkbox
          checked={notifications.length > 0 && selectedNotificationIds.length === notifications.length}
          onCheckedChange={onSelectAll}
          className="cursor-pointer shrink-0"
        />
        <span className="text-sm text-muted-foreground truncate">
          {selectedNotificationIds.length > 0
            ? t('notifications.selected', { count: selectedNotificationIds.length })
            : t('admin.gantt.selectAll')}
        </span>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {unreadCount > 0 && selectedNotificationIds.length === 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onMarkAllRead}
            disabled={markAllReadPending}
            className="cursor-pointer h-8 px-2"
            title={t('notifications.markAllRead')}
          >
            <Check className="w-4 h-4" />
            <span className="hidden sm:inline ml-1">{t('notifications.markAllRead')}</span>
          </Button>
        )}
        {selectedNotificationIds.length > 0 && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={onMarkSelectedAsRead}
              disabled={markReadPending}
              className="cursor-pointer h-8 px-2"
              title={t('notifications.markSelectedAsRead')}
            >
              <Check className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">{t('notifications.markSelectedAsRead')}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDeleteSelected}
              disabled={deleteMultiplePending}
              className="text-destructive hover:text-destructive cursor-pointer h-8 px-2"
              title={t('notifications.deleteSelected')}
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">{t('notifications.deleteSelected')}</span>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function NotificationListContent(props: {
  notifications: Notification[];
  isLoading: boolean;
  selectedNotificationIds: string[];
  onNotificationClick: (n: Notification) => void;
  onToggleSelection: (id: string) => void;
  onMarkRead: (id: string) => void;
  onDelete: (id: string) => void;
  markReadPending: boolean;
  deletePending: boolean;
  getNotificationIcon: (type: string) => React.ReactNode;
  formatDistanceToNow: (d: Date) => string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const {
    notifications,
    isLoading,
    selectedNotificationIds,
    onNotificationClick,
    onToggleSelection,
    onMarkRead,
    onDelete,
    markReadPending,
    deletePending,
    getNotificationIcon,
    formatDistanceToNow,
    t,
  } = props;
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-8 h-8 border-4 border-foreground border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (notifications.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center py-12 text-center p-8">
        <Bell className="w-12 h-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">{t('notifications.empty')}</p>
      </div>
    );
  }
  return (
    <div className="divide-y divide-border min-w-0">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          role="button"
          tabIndex={0}
          className={cn(
            'flex items-start gap-3 p-4 transition-colors cursor-pointer group w-full min-w-0 text-left',
            notification.read ? 'bg-background' : 'bg-primary/5',
            'hover:bg-muted'
          )}
          onPointerDown={(e) => {
            if (e.button === 0) {
              e.preventDefault();
              onNotificationClick(notification);
            }
          }}
          onClick={() => onNotificationClick(notification)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onNotificationClick(notification);
            }
          }}
        >
          <div className="pt-1 shrink-0">
            <Checkbox
              checked={selectedNotificationIds.includes(notification.id)}
              onCheckedChange={() => onToggleSelection(notification.id)}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              className="cursor-pointer"
            />
          </div>
          <div className="shrink-0 mt-1">{getNotificationIcon(notification.type)}</div>
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-start gap-2 min-w-0">
              <p className={cn('text-sm flex-1 min-w-0 notification-item-text', notification.read ? 'font-normal' : 'font-medium')}>
                {notification.title}
              </p>
              {!notification.read && (
                <span className="w-2 h-2 bg-primary rounded-full mt-1.5 shrink-0" />
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1 notification-item-text">
              {notification.message}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {formatDistanceToNow(new Date(notification.createdAt))}
            </p>
          </div>
          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {!notification.read && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkRead(notification.id);
                }}
                disabled={markReadPending}
              >
                <Check className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 cursor-pointer text-destructive hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(notification.id);
              }}
              disabled={deletePending}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function NotificationDropdown({ onNotificationClick }: NotificationDropdownProps) {
  const { t } = useTranslation();
  const formatDistanceToNow = useFormatDistanceToNow();
  const isMobile = useMediaQuery('(max-width: 639px)');
  const {
    notifications,
    unreadCount,
    isSidebarOpen,
    selectedNotificationIds,
    isLoading: storeLoading,
    setNotifications,
    removeNotification,
    removeMultipleNotifications,
    closeSidebar,
    openSidebar,
    setLoading,
    toggleSelection,
    selectAll,
    deselectAll,
    markAsRead,
    markAllAsRead,
  } = useNotificationStore();

  const fetchAndSyncNotifications = useCallback(() => {
    openSidebar();
    setLoading(true);
    api
      .get('/notifications')
      .then((response) => {
        const data = response.data.data as { notifications: Notification[]; unreadCount: number };
        setNotifications(data.notifications);
      })
      .finally(() => setLoading(false));
  }, [openSidebar, setLoading, setNotifications]);

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.patch(`/notifications/${id}/read`);
    },
    onSuccess: (_, id) => {
      markAsRead(id);
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await api.patch('/notifications/mark-all-read');
    },
    onSuccess: () => {
      markAllAsRead();
    },
  });

  // Delete single notification
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/notifications/${id}`);
    },
    onSuccess: (_, id) => {
      removeNotification(id);
    },
  });

  // Delete multiple notifications
  const deleteMultipleMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await api.post('/notifications/delete-multiple', { ids });
    },
    onSuccess: (_, ids) => {
      removeMultipleNotifications(ids);
      deselectAll();
    },
  });

  const lastHandledRef = useRef<{ id: string; at: number } | null>(null);
  const DEDUPE_MS = 300;

  const handleNotificationClick = (notification: Notification) => {
    const now = Date.now();
    const last = lastHandledRef.current;
    if (last?.id === notification.id && now - last.at < DEDUPE_MS) {
      return;
    }
    lastHandledRef.current = { id: notification.id, at: now };
    onNotificationClick?.(notification);
    if (!notification.read) {
      markAsReadMutation.mutate(notification.id);
    }
    closeSidebar();
  };

  const handleSelectAll = () => {
    if (selectedNotificationIds.length === notifications.length) {
      deselectAll();
    } else {
      selectAll(notifications.map((n) => n.id));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedNotificationIds.length > 0) {
      deleteMultipleMutation.mutate(selectedNotificationIds);
    }
  };

  const handleMarkSelectedAsRead = () => {
    if (selectedNotificationIds.length > 0) {
      selectedNotificationIds.forEach((id) => {
        markAsReadMutation.mutate(id);
      });
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'CARD_ASSIGNED':
        return <UserCheck className="w-5 h-5 text-foreground" />;
      case 'COMMENT_MENTION':
        return <MessageSquare className="w-5 h-5 text-foreground" />;
      default:
        return <AlertCircle className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const notificationPanel = (
    <>
      <NotificationPanelHeader
        unreadCount={unreadCount}
        isMobile={isMobile}
        onClose={closeSidebar}
        t={t}
      />
      <NotificationToolbar
        notifications={notifications}
        selectedNotificationIds={selectedNotificationIds}
        unreadCount={unreadCount}
        onSelectAll={handleSelectAll}
        onMarkAllRead={() => markAllAsReadMutation.mutate()}
        onMarkSelectedAsRead={handleMarkSelectedAsRead}
        onDeleteSelected={handleDeleteSelected}
        markAllReadPending={markAllAsReadMutation.isPending}
        markReadPending={markAsReadMutation.isPending}
        deleteMultiplePending={deleteMultipleMutation.isPending}
        t={t}
      />
      <div className="flex-1 overflow-y-auto min-h-0 min-w-0">
        <NotificationListContent
          notifications={notifications}
          isLoading={storeLoading}
          selectedNotificationIds={selectedNotificationIds}
          onNotificationClick={handleNotificationClick}
          onToggleSelection={toggleSelection}
          onMarkRead={(id) => markAsReadMutation.mutate(id)}
          onDelete={(id) => deleteMutation.mutate(id)}
          markReadPending={markAsReadMutation.isPending}
          deletePending={deleteMutation.isPending}
          getNotificationIcon={getNotificationIcon}
          formatDistanceToNow={formatDistanceToNow}
          t={t}
        />
      </div>
    </>
  );

  return (
    <>
      {isMobile ? (
        <>
          <button
            type="button"
            onClick={() => (isSidebarOpen ? closeSidebar() : fetchAndSyncNotifications())}
            className="inline-flex relative p-2 rounded-md hover:bg-muted cursor-pointer"
            aria-label={t('header.notifications')}
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-0.5 flex justify-center items-center min-w-5 h-5 px-1 text-xs font-semibold text-destructive-foreground bg-destructive rounded-full">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          {isSidebarOpen && (
            <>
              <button
                type="button"
                className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
                onClick={closeSidebar}
                aria-label={t('notifications.closeSidebar')}
              />
              <div
                className="fixed left-1/2 top-6 z-50 w-[calc(100vw-2rem)] max-w-[400px] max-h-[calc(100vh-3rem)] -translate-x-1/2 flex flex-col overflow-hidden rounded-md border border-border bg-popover shadow-lg px-4"
              >
                {notificationPanel}
              </div>
            </>
          )}
        </>
      ) : (
        <DropdownMenu open={isSidebarOpen} onOpenChange={(open) => (open ? fetchAndSyncNotifications() : closeSidebar())}>
            <DropdownMenuTrigger asChild>
              <span className="inline-flex relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="cursor-pointer"
                  aria-label={t('header.notifications')}
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-0.5 flex justify-center items-center min-w-5 h-5 px-1 text-xs font-semibold text-destructive-foreground bg-destructive rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Button>
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="p-0 sm:w-[400px] max-h-[calc(100vh-6rem)] flex flex-col min-w-0 overflow-hidden"
            >
              {notificationPanel}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
    </>
  );
}
